import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListFilter,
  FileText,
  Edit,
  Eye,
  Truck,
  PackageCheck,
  Calendar,
  LayoutGrid,
  MoreVertical,
  BookOpen,
  Plus,
  Printer,
  Receipt,
  CheckCircle2
} from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { purchaseOrdersApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

function PurchaseOrders() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const mockOrders = [
    {
      id: 1,
      invoiceNumber: '#23456',
      companyName: 'Deep and Sons Pvt. Ltd.',
      gstin: '234454xxxx',
      netAmount: 44499,
      dateTime: '26/10/2024, 16:09',
      poNumber: 'PO-99283',
      status: 'DELIVERED',
      shippedDate: '27/10',
      deliveredDate: '29/10'
    },
    {
      id: 2,
      invoiceNumber: '#23458',
      companyName: 'MedLife Distribution',
      gstin: '09AAFC8xxxx',
      netAmount: 12850,
      dateTime: '28/10/2024, 09:30',
      poNumber: 'PO-99411',
      status: 'SHIPPED',
      shippedDate: '29/10',
      estDelivery: '31/10'
    },
    {
      id: 3,
      invoiceNumber: '#23451',
      companyName: 'Apex Pharma Supplies',
      gstin: '12HJD89xxxx',
      netAmount: 98210,
      dateTime: '22/10/2024, 11:20',
      poNumber: 'PO-99102',
      status: 'PENDING',
      scheduledDate: '30/10',
      notShipped: true
    },
    {
      id: 4,
      invoiceNumber: '#234510',
      companyName: 'Deep and sons Pvt. Ltd.',
      gstin: '12HJD89xxxx',
      netAmount: 98210,
      dateTime: '22/10/2024, 11:20',
      poNumber: 'PO-99102',
      status: 'PENDING',
      scheduledDate: '30/10',
      notShipped: true
    }
  ];

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      // ALWAYS USE MOCK DATA LOCALLY
      setOrders(mockOrders);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order =>
    (order.companyName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.gstin?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED': return 'bg-[#E7F7EF] text-[#28A745]';
      case 'SHIPPED': return 'bg-[#FFF4E5] text-[#FF9500]';
      case 'PENDING': return 'bg-[#F2F4F7] text-[#667085]';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="p-8 bg-[#FBFDFF] min-h-screen font-sans">
      {/* Top Header */}
      <div className="flex items-center justify-between mb-8 px-2">
        <h1 className="text-[32px] font-bold text-[#1D3557] tracking-tight">Orders</h1>
        <div className="flex items-center space-x-4">
          <button className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm border border-gray-100 text-[#10B981] hover:bg-gray-50 transition-all">
            <ListFilter size={22} />
          </button>
          <button
            onClick={() => navigate('/purchase/orders/create')}
            className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm border border-gray-100 hover:scale-105 transition-all group"
          >
            <div className="relative">
              <Truck size={24} className="text-[#3B82F6]" />
              <div className="absolute -top-1 -right-1 bg-[#10B981] text-white rounded-full w-4 h-4 flex items-center justify-center border-2 border-white shadow-sm">
                <Plus size={10} strokeWidth={4} />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-10 flex justify-start px-2">
        <ExpandableSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by GSTIN number or company name"
          className="rounded-full border-gray-400 shadow-md"
        />
      </div>

      {/* Orders Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-40">
          <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 px-2">
          {filteredOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-[2.5rem] p-8 shadow-[0_10px_40px_rgba(0,0,0,0.06)] border border-gray-100 transition-all hover:shadow-[0_25px_50px_rgba(0,0,0,0.12)] hover:-translate-y-1 h-fit">
              {/* Header: Icon, Info & Actions */}
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center space-x-4">
                  <div className="w-[52px] h-[52px] bg-[#EEF2F4] rounded-2xl flex items-center justify-center text-[#1E4D43]">
                    <Receipt size={26} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-[11px] font-black text-[#10B981] uppercase tracking-widest leading-none mb-1.5">Order</p>
                    <p className="text-[20px] font-black text-[#1E4D43] leading-none">{order.invoiceNumber}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 pt-1">
                  {/* Teal Eye */}
                  <button onClick={() => navigate(`/purchase/orders/${order.id}`)} className="w-[38px] h-[38px] bg-[#22ACC2] rounded-lg flex items-center justify-center text-white hover:scale-110 transition-all shadow-sm">
                    <Eye size={20} strokeWidth={2.5} />
                  </button>

                  {/* Orange Edit */}
                  <button onClick={() => navigate(`/purchase/orders/${order.id}/edit`)} className="w-[38px] h-[38px] bg-[#FFB547] rounded-lg flex items-center justify-center text-white hover:scale-110 transition-all shadow-sm">
                    <Edit size={20} strokeWidth={2.5} />
                  </button>

                  {/* Dark Slate Printer */}
                  <button
                    onClick={() => {
                      // Trigger print logic if available, otherwise just view
                      navigate(`/purchase/orders/${order.id}`);
                    }}
                    className="w-[38px] h-[38px] bg-[#3E4D59] rounded-lg flex items-center justify-center text-white hover:scale-110 transition-all shadow-sm"
                  >
                    <Printer size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Company Info */}
              <div className="mb-6 h-[50px]">
                <p className="text-[22px] font-bold text-[#111827] leading-tight mb-1 line-clamp-2">{order.companyName}</p>
                <div className="flex items-center space-x-1.5">
                  <LayoutGrid className="w-4 h-4 text-[#4B5563]" />
                  <p className="text-[14px] font-bold text-[#4B5563] uppercase tracking-tight">GSTIN: {order.gstin}</p>
                </div>
              </div>

              {/* Net Amount & Date Time */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-[11px] font-black text-[#526484] uppercase tracking-[0.15em] mb-2">Net Amount</p>
                  <p className="text-[28px] font-black text-[#0F172A] flex items-baseline leading-none">
                    <span className="text-[16px] font-bold mr-1 text-[#334155]">₹</span>
                    {order.netAmount?.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-black text-[#526484] uppercase tracking-[0.15em] mb-2">Date & Time</p>
                  <p className="text-[13px] font-bold text-[#334155]">{order.dateTime}</p>
                </div>
              </div>

              {/* PO Number & Status Boxes */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-[#F7FAFC] rounded-2xl p-4 border border-gray-50 flex flex-col justify-center h-[75px]">
                  <p className="text-[10px] font-black text-[#526484] uppercase tracking-[0.1em] mb-2">PO Number</p>
                  <p className="text-[14px] font-black text-[#2D3748] truncate">{order.poNumber}</p>
                </div>
                <div className="bg-[#F7FAFC] rounded-2xl p-4 border border-gray-50 flex flex-col justify-center h-[75px]">
                  <p className="text-[10px] font-black text-[#526484] uppercase tracking-[0.1em] mb-2">Status</p>
                  <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase text-center w-fit ${getStatusColor(order.status)}`}>
                    {order.status}
                  </div>
                </div>
              </div>

              {/* Footer Timeline Info */}
              <div className="flex items-center justify-between opacity-80 border-t border-gray-50 pt-4">
                <div className="flex items-center space-x-2 text-[#334155]">
                  <Truck size={17} strokeWidth={2.5} />
                  <p className="text-[13px] font-bold">
                    Shipped: {order.shippedDate || '27/10'}
                  </p>
                </div>
                <div className="flex items-center space-x-2 text-[#334155]">
                  <CheckCircle2 size={17} strokeWidth={2.5} />
                  <p className="text-[13px] font-bold">
                    {order.deliveredDate ? `Delivered: ${order.deliveredDate}` : `Scheduled: ${order.scheduledDate || '30/10'}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredOrders.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-40 text-gray-200">
          <LayoutGrid size={64} className="mb-4 opacity-20" />
          <p className="text-xl font-black">No orders found</p>
        </div>
      )}
    </div>
  );
}

export default PurchaseOrders;